/* import __COLOCATED_TEMPLATE__ from './operator-workflow-ai-agent-event.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Conversation from 'embercom/objects/inbox/conversation';
import { truncateEventText, isEventTextTruncated } from 'embercom/lib/inbox2/event-text-truncation';
import { task } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { post } from 'embercom/lib/ajax';
import ENV from 'embercom/config/environment';
import type { InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type RouterService from '@ember/routing/router-service';
import type { EntityType } from 'embercom/models/data/entity-types';
import { KNOWLEDGE_HUB_ENTITIES_NAMES } from 'embercom/lib/knowledge-hub/constants';

interface BaseAiAgentEvent {
  type?:
    | 'set_attributes'
    | 'step_reasoning'
    | 'workflow_reasoning'
    | 'conditional_evaluation'
    | 'content_retrieval'
    | 'skipped_steps'
    | 'code_condition_error'
    | 'default';
  i18nKey: string;
  operatorName?: string;
  title?: string;
  reason?: string;
  iconName: InterfaceIconName | null;
}

interface AiAgentDefaultEvent extends BaseAiAgentEvent {
  type: 'default';
  title?: string;
  reason?: string;
  id?: number;
  rulesetId?: number;
  actionId?: number;
  userDisplayName?: string;
}

interface AiAgentSetAttributesEvent extends BaseAiAgentEvent {
  attributeName?: string;
  attributeValue?: string;
  remainingAttributesCount?: number;
  attributes?: { name: string; value: string }[];
  type: 'set_attributes';
}

interface AiAgentTaskStepReasoningEvent extends BaseAiAgentEvent {
  step_number?: string;
  step_description?: string;
  reason?: string;
  type: 'step_reasoning';
  sub_procedure?: string;
  transaction_id?: string;
  procedure_planner_run_id?: string;
}

interface AiAgentWorkflowReasoningEvent extends BaseAiAgentEvent {
  type: 'workflow_reasoning';
  title?: string;
  reason?: string;
  id?: number;
  rulesetId?: number;
}

interface AiAgentConditionalEvaluationEvent extends BaseAiAgentEvent {
  type: 'conditional_evaluation';
  selected_branch_number?: string;
  step_number?: string;
  skipped?: boolean;
  sub_procedure?: string;
}

interface AiAgentSkippedStepsEvent extends BaseAiAgentEvent {
  step_number?: string;
  type: 'skipped_steps';
}

interface AiAgentCodeConditionErrorEvent extends BaseAiAgentEvent {
  type: 'code_condition_error';
  step_number?: string;
  condition?: string;
  error_message?: string;
}

interface RetrievedContent {
  entity_type_id: number;
  entity_id: number;
  title: string;
  url?: string; // URL is computed on frontend
  matched: boolean;
}

interface AiAgentContentRetrievalEvent extends BaseAiAgentEvent {
  type: 'content_retrieval';
  step_number?: string;
  query?: string;
  document_count?: number;
  passage_count?: number;
  retrieved_content?: RetrievedContent[];
}

export type OperatorWorkflowAiAgentEvent =
  | AiAgentSetAttributesEvent
  | AiAgentTaskStepReasoningEvent
  | AiAgentWorkflowReasoningEvent
  | AiAgentConditionalEvaluationEvent
  | AiAgentContentRetrievalEvent
  | AiAgentDefaultEvent
  | AiAgentSkippedStepsEvent
  | AiAgentCodeConditionErrorEvent;

interface ExtendedThoughtsStep {
  reasoning: string;
  step_id: string;
}

interface ExtendedThoughtsResponse {
  error: unknown;
  response: {
    steps_progressed: ExtendedThoughtsStep[];
  };
  answer_bot_transaction_id: string;
}

interface Signature {
  Args: {
    aiAgentEvent: OperatorWorkflowAiAgentEvent;
    operatorName: string;
    conversation: Conversation;
  };
}

export default class OperatorWorkflowAiAgentEventComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: Session;
  @service declare router: RouterService;
  @service declare intercomEventService: any;

  @tracked openStepReasoningSection = '';
  @tracked showExtendedThoughts = false;
  @tracked extendedThoughtsResponse: ExtendedThoughtsResponse | null = null;
  @tracked openCodeErrorSection = '';
  @tracked openContentRetrievalSection = '';
  @tracked openTaskFailedSection = '';

  get eventIcon(): InterfaceIconName {
    // `iconName` may be null on the args type, but we only
    // render it in places that already guard for its presence.
    return this.args.aiAgentEvent.iconName as InterfaceIconName;
  }

  get isDeeperThoughtsEnabled() {
    // Only enabled for Procedure Reasoning which has a transaction_id and procedure_planner_run_id
    let event = this.args.aiAgentEvent;
    return (
      (this.session.workspace?.isFeatureEnabled('answerbot-fin-procedures-extended-reasoning') ??
        false) &&
      event.type === 'step_reasoning' &&
      event.transaction_id &&
      event.procedure_planner_run_id
    );
  }

  get isGeneratingExtendedThoughts() {
    return taskFor(this.generateExtendedThoughts).isRunning;
  }

  get isDevelopmentEnvironment() {
    return ENV.environment === 'development';
  }

  get retrievedContentWithUrls(): RetrievedContent[] {
    let event = this.args.aiAgentEvent;

    if (event.type !== 'content_retrieval' || !event.retrieved_content) {
      return [];
    }

    return event.retrieved_content.map((content) => ({
      ...content,
      url: this.generateContentUrl(content.entity_type_id, content.entity_id),
    }));
  }

  private generateContentUrl(entityTypeId: number, entityId: number): string {
    let entityType = entityTypeId as EntityType;
    let entityTypeName =
      KNOWLEDGE_HUB_ENTITIES_NAMES[entityType as keyof typeof KNOWLEDGE_HUB_ENTITIES_NAMES];

    if (!entityTypeName) {
      console.warn(`Unknown entity_type_id: ${entityTypeId}`);
      return '#';
    }

    let appId = this.session.workspace?.id;
    if (!appId) {
      console.warn('No app_id available for generating content URL');
      return '#';
    }

    return this.router.urlFor('apps.app.automation.fin-ai-agent.content', appId, {
      queryParams: {
        activeContentId: entityId,
        activeContentType: entityTypeName,
        editorMode: 'view',
        native_content: true,
      },
    });
  }

  truncateTitle(title: string | undefined): string {
    return truncateEventText(title);
  }

  isTitleTruncated(title: string | undefined): boolean {
    return isEventTextTruncated(title);
  }

  @action
  toggleStepReasoningSection() {
    let nextState =
      this.openStepReasoningSection === 'open_reasoning_step' ? '' : 'open_reasoning_step';
    this.openStepReasoningSection = nextState;
    if (nextState === 'open_reasoning_step') {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'expanded',
        object: 'fin_thoughts',
        place: 'conversation_stream',
        event_type: 'step_reasoning',
      });
    }
  }

  @action
  toggleCodeErrorSection() {
    this.openCodeErrorSection =
      this.openCodeErrorSection === 'open_code_error' ? '' : 'open_code_error';
  }

  @action
  toggleTaskFailedSection() {
    this.openTaskFailedSection =
      this.openTaskFailedSection === 'open_task_failed' ? '' : 'open_task_failed';
  }

  @action
  toggleContentRetrievalSection() {
    this.openContentRetrievalSection =
      this.openContentRetrievalSection === 'open_content_retrieval' ? '' : 'open_content_retrieval';
  }

  @action
  triggerExtendedThoughts() {
    this.showExtendedThoughts = !this.showExtendedThoughts;
    if (this.showExtendedThoughts) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'expanded',
        object: 'extended_thoughts',
        place: 'conversation_stream',
        conversation_id: this.args.conversation.id,
      });
      if (!this.extendedThoughtsResponse) {
        taskFor(this.generateExtendedThoughts).perform();
      }
    }
  }

  @task({ drop: true })
  *generateExtendedThoughts(): TaskGenerator<void> {
    let event = this.args.aiAgentEvent;
    if (event.type !== 'step_reasoning' || !event.reason) {
      return;
    }

    try {
      let response = yield post(`/ember/operator_workflows/generate_procedure_extended_thoughts`, {
        app_id: this.session.workspace.id,
        conversation_id: this.args.conversation.id,
        transaction_id: event.transaction_id,
        procedure_planner_run_id: event.procedure_planner_run_id,
      });

      if (response) {
        let extended_thoughts = typeof response === 'object' ? response : JSON.parse(response);
        this.extendedThoughtsResponse = extended_thoughts;
      }
    } catch (error: unknown) {
      console.error('Failed to generate procedure conversation insights:', error);
      this.extendedThoughtsResponse = null;
      return;
    }
  }

  get extendedThoughtsEntries(): Array<{ stepName: string; reasoning: string }> {
    if (!this.extendedThoughtsResponse?.response?.steps_progressed) {
      return [];
    }

    return this.extendedThoughtsResponse.response.steps_progressed.map((step) => ({
      stepName: this.formatStepId(step.step_id),
      reasoning: step.reasoning,
    }));
  }

  private formatStepId(stepId: string): string {
    if (stepId.startsWith('main_section.')) {
      let stepNumber = stepId.replace('main_section.', '');
      return this.intl.t('inbox.operator-workflow-event.events.extended-thoughts-step', {
        stepNumber,
      });
    }

    let parts = stepId.split('.');
    let sectionName = parts[0]
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    let stepNumbers = parts.slice(1).join('.');
    return this.intl.t('inbox.operator-workflow-event.events.extended-thoughts-step-with-section', {
      sectionName,
      stepNumbers,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::OperatorWorkflowAiAgentEvent': typeof OperatorWorkflowAiAgentEventComponent;
    'inbox2/conversation-stream/event-parts/operator-workflow-ai-agent-event': typeof OperatorWorkflowAiAgentEventComponent;
  }
}
